/**
 * "The app has just been brought back" as an event.
 *
 * An installed PWA is not reloaded when its icon is tapped. Both Chrome on Android and
 * Safari on iOS keep the page alive in the background — frozen, not destroyed — so
 * reopening it re-runs no queries, remounts no components and fires no navigation. The
 * app comes back showing exactly what was on screen when it was put away, which on a
 * shared shopping list is somebody else's shop from an hour ago.
 *
 * Nothing in the browser reports that in one place, so this listens for all four signals
 * that mean it and publishes one:
 *
 * - `visibilitychange` — the ordinary case, the app coming to the front.
 * - `resume` — Chrome's Page Lifecycle event for a frozen tab thawing. A tab frozen
 *   while already visible (the phone locked, then unlocked to the same screen) thaws
 *   without any visibility change at all.
 * - `pageshow` with `persisted` — restored from the back/forward cache, which runs no
 *   effects and re-renders nothing.
 * - `online` — the network came back, which is a resume in every way that matters here.
 *
 * They overlap constantly, so notifications are coalesced: one resume must not mean four
 * reconciles of the same data.
 */

type Listener = () => void;

/** Several of the events above fire together on a single resume. */
const CoalesceMs = 1_000;

const listeners = new Set<Listener>();
const teardown: (() => void)[] = [];

let started = false;
let lastNotifiedAt = 0;

/**
 * Registers interest in the app being reopened. Returns an unsubscribe.
 *
 * Starting the underlying listeners is idempotent and happens on first subscription, so
 * several components can use this without attaching duplicates — the same arrangement
 * `queueStore` and `installStore` use, and for the same reason.
 */
export function onResume(listener: Listener): () => void {
  start();
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function start(): void {
  if (started || typeof document === 'undefined') {
    return;
  }

  started = true;

  listen(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      notify();
    }
  });

  listen(document, 'resume', notify);

  listen(window, 'pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) {
      notify();
    }
  });

  listen(window, 'online', notify);
}

function listen(target: EventTarget, type: string, handler: (event: Event) => void): void {
  target.addEventListener(type, handler);
  teardown.push(() => target.removeEventListener(type, handler));
}

function notify(): void {
  const now = Date.now();

  if (now - lastNotifiedAt < CoalesceMs) {
    return;
  }

  lastNotifiedAt = now;

  // Copied, because a listener is allowed to unsubscribe itself while being called.
  for (const listener of [...listeners]) {
    listener();
  }
}

/** Test seam: detaches the real listeners and resets module state between cases. */
export function resetResumeForTests(): void {
  for (const detach of teardown) {
    detach();
  }

  teardown.length = 0;
  listeners.clear();
  started = false;
  lastNotifiedAt = 0;
}
